import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
// eslint-disable-next-line import/no-unresolved
import { AddLimitRequestCreate, Box } from "@ui-kit/components/index.es";
import classNames from "classnames";
import Head from "pages/_components/Head";
import Notification from "pages/_components/Notification";
import PropTypes, { shape } from "prop-types";
import { connect } from "react-redux";
import { compose } from "redux";
import { goBack, push } from "react-router-redux";
import { actions as addLimitRequestActions, selectors as addLimitRequestSelectors } from "reducers/addLimitRequest";
import { actions as notificationActions } from "reducers/notification";
import * as configUtils from "util/config";
import * as i18n from "util/i18n";
import yup from "yup";
import { resizableRoute } from "pages/_components/Resizable";
import { applyEmbeddedV3ShellLayoutState, clearEmbeddedV3ShellLayoutState } from "util/v3ShellLayoutSync";
import {
    buildAddLimitRequestConfirmationLocation,
    getPrevPathFromLocation,
    resolveAddLimitGoBackTarget,
} from "util/addLimitRequestNavigation";

const FORM_ID = "addLimitRequest.create";

const getSelectValue = (field) => {
    if (field && typeof field === "object" && field.value !== undefined) {
        return field.value;
    }
    return field;
};

const hasSignatureSchemeForMotive = (motiveValue, limitsByReason, loadingLimits) => {
    if (!motiveValue || loadingLimits || limitsByReason == null) {
        return true;
    }
    return limitsByReason[motiveValue]?.signatureLimit != null;
};

const parseAmountToNumber = (value) => {
    if (!value) {
        return 0;
    }
    const cleanValue = value
        .toString()
        .replace(/\./g, "")
        .replace(",", ".");
    const parsed = parseFloat(cleanValue);
    return Number.isNaN(parsed) ? 0 : parsed;
};

const toBaseCurrencyAmount = (amount, currencyValue) => {
    if (currencyValue === "2") {
        const dolarQuot = parseFloat(configUtils.get("CMF_Dolar_Valor", "98.90"));
        return Math.trunc((amount * dolarQuot + Number.EPSILON) * 100) / 100;
    }
    return amount;
};

const createBelowSignatureLimitTest = (limitsByReason, getFormLabel) =>
    function validateSignatureLimit(value) {
        const motiveValue = getSelectValue(this.parent.motive);
        if (!motiveValue || !value || !limitsByReason) {
            return true;
        }

        const signatureLimit = limitsByReason[motiveValue]?.signatureLimit;
        if (signatureLimit == null) {
            return true;
        }

        const amountInBaseCurrency = toBaseCurrencyAmount(
            parseAmountToNumber(value),
            getSelectValue(this.parent.currency),
        );

        if (amountInBaseCurrency <= signatureLimit) {
            return this.createError({ message: getFormLabel("belowSignatureLimit") });
        }
        return true;
    };

const AddLimitRequest = (props) => {
    const {
        isDesktop,
        dispatch,
        isFetching,
        embeddedInV3Shell,
        location,
        navigationPrevPath,
        loadingLimits,
        limitsByReason,
        environmentLimit,
    } = props;

    const [canContinue, setCanContinue] = useState(false);
    const formikRef = useRef(null);
    const lastEnvWarningKeyRef = useRef(null);
    const lastNoSignatureWarningKeyRef = useRef(null);

    const goBackTarget = useMemo(() => resolveAddLimitGoBackTarget(location, navigationPrevPath), [
        location,
        navigationPrevPath,
    ]);

    const getFormLabel = useCallback((id) => {
        const defaultLabels = {
            motive: "Motivo",
            temporaryLimit: "Límite temporal",
            duration: "Duración límite temporal",
            continue: "Continuar",
            selectPlaceholder: "Seleccioná",
            maxAllowedLimit: "Límite máximo permitido:",
            belowSignatureLimit: "El limite que estás ingresando es menor al que ya tenes configurado",
            exceedsEnvironmentLimit: "El límite ingresado supera el máximo permitido del ambiente",
            noSignatureSchemeAvailable: "No tenes esquema de firmas disponibles",
        };
        return i18n.get(`${FORM_ID}.${id}`, defaultLabels[id] || id);
    }, []);

    useEffect(() => {
        dispatch(addLimitRequestActions.cleanData());
        dispatch(addLimitRequestActions.loadPreLimits());
    }, [dispatch]);

    useEffect(() => {
        const prevPath = getPrevPathFromLocation(location);
        if (prevPath) {
            dispatch(addLimitRequestActions.setPrevPath(prevPath));
        }
    }, [dispatch, location]);

    useEffect(() => {
        applyEmbeddedV3ShellLayoutState(dispatch, {
            embeddedInV3Shell,
            isDesktop,
            scopes: ["addLimitRequest"],
            titleKey: `${FORM_ID}.title`,
            goBackPath: goBackTarget,
        });
        return () => {
            if (embeddedInV3Shell) {
                clearEmbeddedV3ShellLayoutState(dispatch);
            }
        };
    }, [dispatch, embeddedInV3Shell, isDesktop, goBackTarget]);

    const getAmountInBaseCurrency = useCallback((values) => {
        const currencyValue = getSelectValue(values.currency);
        return toBaseCurrencyAmount(parseAmountToNumber(values.temporaryLimit), currencyValue);
    }, []);

    const validationSchema = useMemo(
        () =>
            yup.object().shape({
                motive: yup.mixed().required(i18n.get("forms.openingAccount.fieldRequired", "El campo es requerido")),
                temporaryLimit: yup
                    .string()
                    .required(i18n.get("forms.openingAccount.fieldRequired", "El campo es requerido"))
                    .test("belowSignatureLimit", createBelowSignatureLimitTest(limitsByReason, getFormLabel)),
                currency: yup.mixed().required(i18n.get("forms.openingAccount.fieldRequired", "El campo es requerido")),
                duration: yup.mixed().required(i18n.get("forms.openingAccount.fieldRequired", "El campo es requerido")),
            }),
        [getFormLabel, limitsByReason],
    );

    const showNoSignatureSchemeWarning = useCallback(
        (motiveValue) => {
            if (loadingLimits || limitsByReason == null || !motiveValue) {
                return;
            }

            if (hasSignatureSchemeForMotive(motiveValue, limitsByReason, loadingLimits)) {
                lastNoSignatureWarningKeyRef.current = null;
                return;
            }

            const warningKey = `no-sig-${motiveValue}`;
            if (lastNoSignatureWarningKeyRef.current !== warningKey) {
                lastNoSignatureWarningKeyRef.current = warningKey;
                dispatch(
                    notificationActions.showNotification(getFormLabel("noSignatureSchemeAvailable"), "warning", [
                        "addLimitRequest",
                    ]),
                );
            }
        },
        [dispatch, getFormLabel, limitsByReason, loadingLimits],
    );

    const validateAmountBounds = useCallback(
        (values) => {
            const motiveValue = getSelectValue(values.motive);
            if (motiveValue && !hasSignatureSchemeForMotive(motiveValue, limitsByReason, loadingLimits)) {
                setCanContinue(false);
                return false;
            }

            const durationValue = getSelectValue(values.duration);
            if (!motiveValue || !values.temporaryLimit || !durationValue) {
                setCanContinue(false);
                return false;
            }

            const signatureLimit = limitsByReason?.[motiveValue]?.signatureLimit;
            const amountInBaseCurrency = getAmountInBaseCurrency(values);
            if (signatureLimit != null) {
                if (amountInBaseCurrency <= signatureLimit) {
                    setCanContinue(false);
                    return false;
                }
            }

            if (environmentLimit != null && amountInBaseCurrency > environmentLimit) {
                setCanContinue(false);
                return false;
            }

            setCanContinue(true);
            return true;
        },
        [environmentLimit, getAmountInBaseCurrency, limitsByReason, loadingLimits],
    );

    const showEnvironmentLimitWarning = useCallback(
        (values) => {
            const motiveValue = getSelectValue(values.motive);
            if (!motiveValue || !values.temporaryLimit || environmentLimit == null) {
                return;
            }

            const amountInBaseCurrency = getAmountInBaseCurrency(values);
            const warningKey = `${motiveValue}-${values.temporaryLimit}-${getSelectValue(values.currency)}`;

            if (amountInBaseCurrency > environmentLimit) {
                if (lastEnvWarningKeyRef.current !== warningKey) {
                    lastEnvWarningKeyRef.current = warningKey;
                    dispatch(
                        notificationActions.showNotification(getFormLabel("exceedsEnvironmentLimit"), "warning", [
                            "addLimitRequest",
                        ]),
                    );
                }
                return;
            }

            lastEnvWarningKeyRef.current = null;
        },
        [dispatch, environmentLimit, getAmountInBaseCurrency, getFormLabel],
    );

    const validateTemporaryLimitField = useCallback(
        (values, formik, { touch = false } = {}) => {
            validateAmountBounds(values);
            if (touch) {
                formik.setFieldTouched("temporaryLimit", true, false);
            }
            formik.validateField("temporaryLimit");
        },
        [validateAmountBounds],
    );

    const handleSetFormikProps = useCallback((formikProps) => {
        formikRef.current = formikProps;
    }, []);

    useEffect(() => {
        if (formikRef.current && limitsByReason && !loadingLimits) {
            const { values } = formikRef.current;
            validateAmountBounds(values);
            showNoSignatureSchemeWarning(getSelectValue(values.motive));
        }
    }, [limitsByReason, loadingLimits, validateAmountBounds, showNoSignatureSchemeWarning]);

    const handleBack = () => {
        if (embeddedInV3Shell) {
            dispatch(push(goBackTarget));
            return;
        }
        dispatch(goBack());
    };

    const handleMotiveChange = useCallback(
        (motive, formik) => {
            lastEnvWarningKeyRef.current = null;
            formik.setFieldValue("temporaryLimit", "");
            formik.setFieldValue("duration", null);
            formik.setFieldValue("currency", "0");
            formik.setFieldError("temporaryLimit", undefined);
            formik.setFieldError("duration", undefined);
            formik.setFieldError("currency", undefined);
            formik.setFieldTouched("temporaryLimit", false, false);
            formik.setFieldTouched("duration", false, false);
            formik.setFieldTouched("currency", false, false);
            const motiveValue = getSelectValue(motive);
            showNoSignatureSchemeWarning(motiveValue);
            validateAmountBounds({
                ...formik.values,
                motive,
                temporaryLimit: "",
                duration: null,
                currency: "0",
            });
        },
        [showNoSignatureSchemeWarning, validateAmountBounds],
    );

    const handleTemporaryLimitBlur = useCallback(
        (values, formik) => {
            validateTemporaryLimitField(values, formik, { touch: true });
            showEnvironmentLimitWarning(values);
        },
        [showEnvironmentLimitWarning, validateTemporaryLimitField],
    );

    const handleDurationChange = useCallback(
        (duration, formik) => {
            validateTemporaryLimitField({ ...formik.values, duration }, formik);
        },
        [validateTemporaryLimitField],
    );

    const handleTemporaryLimitChange = useCallback(
        (values, formik) => {
            validateTemporaryLimitField(values, formik);
        },
        [validateTemporaryLimitField],
    );

    const initialValues = {
        motive: "",
        temporaryLimit: "",
        currency: "0",
        duration: "",
    };

    const handleSubmit = (values) => {
        if (!validateAmountBounds(values)) {
            return;
        }

        const getSelectLabel = (field) => {
            if (field && typeof field === "object" && field.label !== undefined) {
                return field.label;
            }
            return field;
        };

        const currencyMap = { "0": "ARS", "2": "USD" };

        const formatAmountToDecimal = (value) => {
            if (!value) {
                return "0";
            }
            const cleanValue = value
                .toString()
                .replace(/\./g, "")
                .replace(",", ".");
            return cleanValue;
        };

        const motiveValue = getSelectValue(values.motive);
        const motiveLabel = getSelectLabel(values.motive);
        const durationValue = getSelectValue(values.duration);
        const durationLabel = getSelectLabel(values.duration);
        const currencyValue = getSelectValue(values.currency);

        const requestData = {
            motive: motiveLabel,
            motiveValue,
            temporaryLimit: formatAmountToDecimal(values.temporaryLimit),
            currency: currencyMap[currencyValue] || "ARS",
            currencyValue,
            duration: durationLabel,
            durationValue,
        };
        dispatch(addLimitRequestActions.setRequestData(requestData));
        dispatch(
            push(buildAddLimitRequestConfirmationLocation(getPrevPathFromLocation(location) ?? navigationPrevPath)),
        );
    };

    return (
        <>
            {!embeddedInV3Shell && (
                <Notification scopeToShow="addLimitRequest" notificationClassname={!isDesktop ? "z-index-9" : ""} />
            )}
            <div className={`admin-detail-head ${isDesktop ? "px-4" : "d-block"}`}>
                {(!embeddedInV3Shell || isDesktop) && (
                    <>
                        <Head
                            onBack={handleBack}
                            title={!isDesktop ? `${FORM_ID}.title` : undefined}
                            titleClassName="text-white"
                            accessibilityTextId={`${FORM_ID}.title`}
                            headerClassName={classNames({
                                "blue-main-header-mobile": !isDesktop,
                            })}
                            centerContentClassName={classNames({
                                "mx-5": !isDesktop,
                            })}
                        />
                        {isDesktop ? (
                            <Head
                                title={`${FORM_ID}.title`}
                                titleText={i18n.get(`${FORM_ID}.title`)}
                                accessibilityTextId={`${FORM_ID}.title`}
                                messageTooltip="frontend.AddLimitRequest.TooltipHeader"
                                hintText={configUtils.get("frontend.text.temporarilyincreaselimit")}
                                alwaysShowMessage
                            />
                        ) : null}
                    </>
                )}
                <Box mt={isDesktop ? 32 : 0}>
                    <AddLimitRequestCreate
                        initialValues={initialValues}
                        onSubmit={handleSubmit}
                        setFormikProps={handleSetFormikProps}
                        hintText={configUtils.get("frontend.text.temporarilyincreaselimit")}
                        validationSchema={validationSchema}
                        getFormLabel={getFormLabel}
                        loading={isFetching}
                        maxAmountAllowedLength={24}
                        limitsByReason={limitsByReason}
                        loadingLimits={loadingLimits}
                        onMotiveChange={handleMotiveChange}
                        onDurationChange={handleDurationChange}
                        onTemporaryLimitBlur={handleTemporaryLimitBlur}
                        onTemporaryLimitChange={handleTemporaryLimitChange}
                        canContinue={canContinue}
                    />
                </Box>
            </div>
        </>
    );
};

AddLimitRequest.propTypes = {
    isDesktop: PropTypes.bool.isRequired,
    dispatch: PropTypes.func.isRequired,
    isFetching: PropTypes.bool,
    embeddedInV3Shell: PropTypes.bool,
    navigationPrevPath: PropTypes.string,
    loadingLimits: PropTypes.bool,
    limitsByReason: PropTypes.shape({}),
    environmentLimit: PropTypes.number,
    location: shape({
        pathname: PropTypes.string,
        state: PropTypes.shape({}),
    }),
};

AddLimitRequest.defaultProps = {
    isFetching: false,
    embeddedInV3Shell: false,
    navigationPrevPath: null,
    loadingLimits: false,
    limitsByReason: null,
    environmentLimit: null,
    location: undefined,
};

const mapStateToProps = (state) => ({
    isFetching: addLimitRequestSelectors.getFetching(state),
    navigationPrevPath: addLimitRequestSelectors.getPrevPath(state),
    loadingLimits: addLimitRequestSelectors.getLoadingLimits(state),
    limitsByReason: addLimitRequestSelectors.getLimitsByReason(state),
    environmentLimit: addLimitRequestSelectors.getEnvironmentLimit(state),
});

export default compose(connect(mapStateToProps), resizableRoute)(AddLimitRequest);
